import { forwardRef, Fragment, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CompoundEntityRef,
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@mainstage/catalog-model';
import {
  EntityDisplayName,
  entityRouteRef,
} from '@mainstage/plugin-catalog-react';
import { useRouteRef } from '@mainstage/core-plugin-api';
import { WbLink } from '../WbLink';

export interface WbEntityRefLinkProps {
  entityRef: string | CompoundEntityRef | Entity;
  defaultKind?: string;
  defaultNamespace?: string;
  title?: string;
  children?: ReactNode;
  hideIcon?: boolean;
  basePath?: string;
  [key: string]: any;
}

export const useEntityRefLink = (
  eRef: string | CompoundEntityRef | Entity,
  defaultKind: string = 'component',
  defaultNamespace: string = 'default',
  basePath?: string,
) => {
  const stringRef = typeof eRef === 'string' ? eRef : stringifyEntityRef(eRef);

  const parsed = parseEntityRef(stringRef, { defaultKind, defaultNamespace });
  const location = useLocation();
  const currentPath = location.pathname;
  const eRoute = useRouteRef(entityRouteRef);

  const base =
    basePath ||
    (currentPath.startsWith('/platform-settings') ? '/platform-settings' : '');

  return `${base}${eRoute(parsed)}`;
};

export const WbEntityRefLink = forwardRef<
  HTMLAnchorElement,
  WbEntityRefLinkProps
>((props, ref) => {
  const {
    entityRef,
    defaultKind = 'component',
    defaultNamespace = 'default',
    title,
    children,
    hideIcon,
    basePath,
    ...linkProps
  } = props;

  const entityRoute = useEntityRefLink(entityRef);

  const content = children ?? title ?? (
    <EntityDisplayName
      entityRef={entityRef}
      defaultKind={defaultKind}
      defaultNamespace={defaultNamespace}
      hideIcon={hideIcon}
    />
  );

  return (
    <WbLink {...linkProps} block-row-click="true" ref={ref} to={entityRoute}>
      {content}
    </WbLink>
  );
});

export interface WbEntityRefLinksProps {
  entityRefs: string[] | CompoundEntityRef[] | Entity[];
  defaultKind?: string;
  defaultNamespace?: string;
  basePath?: string;
  hideIcon?: boolean;
  [key: string]: any;
}

export const WbEntityRefLinks = ({
  entityRefs,
  defaultKind = 'component',
  defaultNamespace = 'default',
  basePath,
  hideIcon,
  ...linkProps
}: WbEntityRefLinksProps) => {
  return (
    <>
      {entityRefs.map((ref, index) => {
        return (
          <Fragment key={index}>
            {index > 0 && ', '}
            <WbEntityRefLink
              entityRef={ref}
              defaultKind={defaultKind}
              defaultNamespace={defaultNamespace}
              basePath={basePath}
              hideIcon={hideIcon}
              {...linkProps}
            />
          </Fragment>
        );
      })}
    </>
  );
};
